import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { useTranslation } from '../../i18n';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  FaPlus, FaEllipsisV, FaChevronDown, FaDatabase, FaBookmark,
  FaComment, FaTable, FaChartBar,
  FaQuestionCircle, FaFileContract, FaLightbulb, FaCog,
  FaSignOutAlt, FaFile, FaFileExcel, FaFileCsv, FaFilePdf, FaUpload, FaTrash,
  FaBrain, FaTimes, FaRocket, FaEdit, FaCheck, FaFileAlt,
  FaThList, FaBullhorn, FaAd, FaGlobe, FaGift,
  FaNewspaper, FaTiktok, FaInstagram,
  FaShoppingBasket, FaHashtag, FaStore, FaGlobeAmericas
} from 'react-icons/fa';
import "./sidebarmenu.css";
import logoDefault from '../../assets/img/logo-data-new.png';
import { useAuthenticator } from '@aws-amplify/ui-react';
import logoChat from '../../assets/img/logo-chat.png';
import useActivityTracker from '../../hooks/useActivityTracker';

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';
const endpoint_get = '/get_conversations_user';
const endpoint_add = '/add_conversation';
const endpoint_delete = '/delete_conversation';
const endpoint_get_saved_consults = '/get_saved_consults';
const endpoint_delete_consult = '/delete_consult';

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTES AUXILIARES
// ═══════════════════════════════════════════════════════════════════════════════

// Componente para módulo principal (nivel 1)
const ModuleHeader = ({ title, icon, isOpen, onClick, badge, isLink, disabled, disabledTooltip }) => (
  <button
    className={`module-header ${isOpen ? 'active' : ''} ${isLink ? 'link-only' : ''} ${disabled ? 'disabled' : ''}`}
    onClick={disabled ? undefined : onClick}
    title={disabled ? disabledTooltip : undefined}
  >
    <span className="module-icon">{icon}</span>
    <span className="module-title">{title}</span>
    {badge && !disabled && <span className="module-badge">{badge}</span>}
    {!isLink && !disabled && <FaChevronDown className={`module-chevron ${isOpen ? 'rotated' : ''}`} />}
  </button>
);

// Item plano de navegación (maqueta cliente — Cantabria Labs)
// Filas simples icono + etiqueta. Los reales son clickeables; los de demo
// se muestran deshabilitados (en gris) con tooltip "Próximamente".
const NavItem = ({ icon, label, badge, badgeType, isActive, disabled, disabledTooltip, expandable, isOpen, onClick }) => (
  <button
    type="button"
    className={`nav-item ${isActive ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
    onClick={disabled ? undefined : onClick}
    title={disabled ? disabledTooltip : undefined}
  >
    <span className="nav-item-icon">{icon}</span>
    <span className="nav-item-label">{label}</span>
    {badge && <span className={`nav-badge ${badgeType || 'new'}`}>{badge}</span>}
    {expandable && <FaChevronDown className={`nav-item-chevron ${isOpen ? 'rotated' : ''}`} />}
  </button>
);

// Grupo plegable del menú (Social Media, Publicidad, Comercial, Web y Medios).
// Si ninguno de sus items aplica al usuario (p.ej. Comercial en demo), no se pinta.
// El encabezado usa el mismo NavItem expandible que AI Insights.
const NavSection = ({ id, title, icon, isOpen, isActive, onToggle, children }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div className="nav-group nav-section">
      <NavItem
        icon={icon}
        label={title}
        expandable
        isOpen={isOpen}
        isActive={isActive && !isOpen}
        onClick={() => onToggle(id)}
      />
      {isOpen && <div className="nav-section-items">{items}</div>}
    </div>
  );
};

// A qué grupo pertenece cada ruta: el grupo de la pantalla actual se abre solo.
const SECTION_BY_PATH = {
  '/': 'social',
  '/social-organic': 'social',
  '/campaigns': 'advertising',
  '/campaigns-api': 'advertising',
  '/campaigns-tiktok': 'advertising',
  '/venta': 'commercial',
  '/beneficios': 'commercial',
  '/andromedapp': 'commercial',
  '/traffic': 'webMedia',
  '/noticias': 'webMedia',
};

const SECTIONS_STORAGE_KEY = 'sidebar_open_sections';

const loadOpenSections = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(SECTIONS_STORAGE_KEY) || 'null');
    if (Array.isArray(saved)) return saved;
  } catch (e) { /* ignorar errores de localStorage */ }
  return ['social'];
};

// Componente para subitem
const SubItem = ({ icon, label, onClick, isActive, onDelete, showDelete }) => (
  <div className={`sub-item ${isActive ? 'active' : ''}`} onClick={onClick}>
    <span className="sub-icon">{icon}</span>
    <span className="sub-label">{label}</span>
    {showDelete && (
      <button className="sub-delete" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
        <FaEllipsisV size={12} />
      </button>
    )}
  </div>
);

// Componente para el diccionario de datos
const DataDictionaryItem = ({ tableName, description, columns, isOpen, toggleTable }) => (
  <div className="dict-item">
    <button className={`dict-header ${isOpen ? 'open' : ''}`} onClick={() => toggleTable(tableName)}>
      <FaTable className="dict-icon" />
      <span className="dict-name">{tableName.replace('public.', '')}</span>
      <span className="dict-count">{columns?.length || 0}</span>
      <FaChevronDown className={`dict-chevron ${isOpen ? 'rotated' : ''}`} />
    </button>
    {isOpen && (
      <div className="dict-content">
        {description && <p className="dict-description">{description}</p>}
        <div className="dict-columns">
          {columns.map((col, idx) => (
            <div key={idx} className="dict-column">
              <div className="col-header">
                <span className="col-name">{col.name}</span>
                <span className="col-type">{col.type}</span>
              </div>
              {col.comment && <span className="col-comment">{col.comment}</span>}
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// Helper para obtener icono según extensión del archivo
const getFileIcon = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'xlsx':
    case 'xls':
      return <FaFileExcel className="file-icon excel" />;
    case 'csv':
      return <FaFileCsv className="file-icon csv" />;
    case 'pdf':
      return <FaFilePdf className="file-icon pdf" />;
    default:
      return <FaFile className="file-icon" />;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

const SidebarMenu = ({
  isSidebarVisible,
  toggleSidebar,
  setMessage,
  onUserConfigLoaded,
  availableReports = [],
  currentReportId,
  currentReportPage,
  onReportChange,
  onReportPageChange,
  userFeatures = {},
  featuresLoaded = false
}) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const navigate = useNavigate();
  const location = useLocation();
  const { trackAction } = useActivityTracker(user?.username, 'navigation');
  const { t, language } = useTranslation();

  // Contexto de organización (multi-tenant)
  const {
    logoUrl: orgLogoUrl,
    displayName: orgDisplayName,
    appTitle: orgAppTitle,
    updateBrandingFromUserConfig,
    loading: orgLoading
  } = useOrganization();

  // Estados principales
  const [openModule, setOpenModule] = useState(null);
  const [openSubSection, setOpenSubSection] = useState(null);
  const [openSections, setOpenSections] = useState(loadOpenSections);
  const [expandedReports, setExpandedReports] = useState({});
  const [openTable, setOpenTable] = useState(null);

  // Estados de datos
  const [conversations, setConversations] = useState([]);
  const [savedQueries, setSavedQueries] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [customReports, setCustomReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingQueries, setIsLoadingQueries] = useState(true);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [isLoadingReports, setIsLoadingReports] = useState(true);

  // Estados de usuario
  const [userConfig, setUserConfig] = useState(null);
  const [organizationLogo, setOrganizationLogo] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ type: '', id: '', name: '' });

  // File upload
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Agent memory modal
  const [memoryModalOpen, setMemoryModalOpen] = useState(false);
  const [agentMemory, setAgentMemory] = useState('');
  const [isLoadingMemory, setIsLoadingMemory] = useState(false);
  const [isSavingMemory, setIsSavingMemory] = useState(false);

  // Title editing states
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  // Data dictionary state (loaded from API)
  const [dataDictionary, setDataDictionary] = useState([]);
  const [isLoadingDictionary, setIsLoadingDictionary] = useState(false);

  // Obtener conversación activa de la URL
  const currentPath = location.pathname;
  const activeChatId = currentPath.includes('/chat/')
    ? currentPath.split('/chat/')[1]
    : (currentPath.includes('/geo/') ? currentPath.split('/geo/')[1] : null);

  // ─────────────────────────────────────────────────────────────────────────────
  // FUNCIONES DE TOGGLE
  // ─────────────────────────────────────────────────────────────────────────────

  const toggleModule = (moduleName) => {
    setOpenModule(openModule === moduleName ? null : moduleName);
    setOpenSubSection(null);
  };

  const toggleSubSection = (sectionName) => {
    setOpenSubSection(openSubSection === sectionName ? null : sectionName);
  };

  const toggleReportExpansion = (reportId) => {
    setExpandedReports(prev => ({ ...prev, [reportId]: !prev[reportId] }));
  };

  const toggleSection = (sectionId) => {
    setOpenSections(prev => (
      prev.includes(sectionId) ? prev.filter(s => s !== sectionId) : [...prev, sectionId]
    ));
  };

  // Navega a una pantalla del menú y, en móvil, cierra el sidebar.
  const goTo = (path) => {
    navigate(path);
    if (window.innerWidth < 768 && toggleSidebar) toggleSidebar();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // El grupo de la pantalla actual siempre queda abierto (p.ej. al entrar por URL).
  useEffect(() => {
    const section = SECTION_BY_PATH[location.pathname];
    if (section) {
      setOpenSections(prev => (prev.includes(section) ? prev : [...prev, section]));
    }
  }, [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(openSections));
    } catch (e) { /* ignorar errores de localStorage */ }
  }, [openSections]);

  // Cargar config de usuario
  useEffect(() => {
    const fetchUserConfig = async () => {
      if (user) {
        try {
          const response = await fetch(
            `${STRANDS_API_URL}/api/user/config?username=${encodeURIComponent(user.username)}`
          );
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setUserConfig(data);
              if (data.organization?.urlImage) {
                setOrganizationLogo(data.organization.urlImage);
                setLogoError(false);
              }
              if (data.role?.toLowerCase() === 'admin') {
                setIsAdmin(true);
              }
              // Actualizar branding de la organización en el contexto (multi-tenant)
              if (updateBrandingFromUserConfig) {
                updateBrandingFromUserConfig(data);
              }
              if (typeof onUserConfigLoaded === 'function') {
                onUserConfigLoaded(data);
              }
            }
          }
        } catch (error) {
          console.error('[SIDEBAR] Error fetching user config:', error);
        }
      }
    };
    fetchUserConfig();
  }, [user, onUserConfigLoaded, updateBrandingFromUserConfig]);

  // Cargar conversaciones y consultas guardadas
  useEffect(() => {
    const fetchConversations = async () => {
      if (user) {
        try {
          setIsLoading(true);
          const url = `${STRANDS_API_URL}${endpoint_get}?id_user=${encodeURIComponent(user.username)}`;
          const res = await fetch(url);
          const response = await res.json();
          if (response?.conversations) {
            const sorted = [...response.conversations].sort((a, b) => b.id_conversation - a.id_conversation);
            setConversations(sorted);
          }
        } catch (error) {
          console.error('Error loading conversations:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    const fetchSavedQueries = async () => {
      if (user) {
        try {
          setIsLoadingQueries(true);
          const url = `${STRANDS_API_URL}${endpoint_get_saved_consults}?id_user=${encodeURIComponent(user.username)}`;
          const res = await fetch(url);
          const response = await res.json();
          if (response?.saved_consults) {
            const sorted = [...response.saved_consults].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setSavedQueries(sorted);
          }
        } catch (error) {
          console.error('Error loading saved queries:', error);
        } finally {
          setIsLoadingQueries(false);
        }
      }
    };

    const fetchUploadedFiles = async () => {
      if (user) {
        try {
          setIsLoadingFiles(true);
          const response = await fetch(
            `${STRANDS_API_URL}/api/files/available?username=${encodeURIComponent(user.username)}`
          );
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.files) {
              setUploadedFiles(data.files);
            }
          }
        } catch (error) {
          console.error('[SIDEBAR] Error loading files:', error);
        } finally {
          setIsLoadingFiles(false);
        }
      }
    };

    const fetchCustomReports = async () => {
      if (user) {
        try {
          setIsLoadingReports(true);
          const url = `${STRANDS_API_URL}/api/reports?username=${encodeURIComponent(user.username)}`;
          const res = await fetch(url);
          const response = await res.json();
          if (response?.success && response.reports) {
            setCustomReports(response.reports);
          }
        } catch (error) {
          console.error('[SIDEBAR] Error loading custom reports:', error);
        } finally {
          setIsLoadingReports(false);
        }
      }
    };

    fetchConversations();
    fetchSavedQueries();
    fetchUploadedFiles();
    fetchCustomReports();
  }, [user]);

  // Refrescar la lista de Reportes Personalizados cuando otro componente
  // (típicamente el chat al usar "Guardar en reporte" → nuevo) crea uno
  // nuevo. Antes el usuario tenía que recargar la página para verlo.
  useEffect(() => {
    if (!user) return;
    const refetch = async () => {
      try {
        const url = `${STRANDS_API_URL}/api/reports?username=${encodeURIComponent(user.username)}`;
        const res = await fetch(url);
        const response = await res.json();
        if (response?.success && response.reports) {
          setCustomReports(response.reports);
        }
      } catch (error) {
        console.error('[SIDEBAR] Error refrescando lista de reportes:', error);
      }
    };
    document.addEventListener('reportCreated', refetch);
    document.addEventListener('reportListChanged', refetch);
    return () => {
      document.removeEventListener('reportCreated', refetch);
      document.removeEventListener('reportListChanged', refetch);
    };
  }, [user]);

  // Cargar diccionario de datos desde API
  useEffect(() => {
    const fetchDataDictionary = async () => {
      if (user) {
        try {
          setIsLoadingDictionary(true);
          const response = await fetch(
            `${STRANDS_API_URL}/api/user/data-dictionary?username=${encodeURIComponent(user.username)}`
          );
          if (response.ok) {
            const data = await response.json();
            // Aceptar tanto 'data_dictionary' como 'dictionary' (el backend usa 'dictionary')
            const dictData = data.data_dictionary || data.dictionary;
            if (data.success && dictData) {
              // Transformar la estructura de API al formato del componente
              // API retorna: { tables: { "tableName": [ {Name, Type, Comment} ] } }
              // Componente espera: [{ tableName, description, columns }]
              let transformedDictionary = [];

              // Helper para extraer comentario del idioma correcto
              const getLocalizedComment = (comment) => {
                if (typeof comment === 'string') return comment;
                if (comment && typeof comment === 'object') {
                  return comment[language] || comment.es || comment.en || '';
                }
                return '';
              };

              if (dictData.tables && typeof dictData.tables === 'object') {
                // Soportar ambas estructuras:
                // 1. RobustAgentConfig: { tables: { "tableName": [ {Name, Type, Comment: {es, en}} ] } }
                // 2. Estructura con descripción: { tables: { "tableName": { description, columns } } }
                transformedDictionary = Object.entries(dictData.tables).map(([tableName, tableData]) => {
                  // Detectar si tableData es un array (formato RobustAgentConfig) o un objeto
                  if (Array.isArray(tableData)) {
                    // Formato RobustAgentConfig: array de campos {Name, Type, Comment}
                    return {
                      tableName: tableName,
                      description: '', // No hay descripción en este formato
                      columns: tableData.map(field => ({
                        name: field.Name || field.name || '',
                        type: field.Type || field.type || 'string',
                        comment: getLocalizedComment(field.Comment || field.comment)
                      }))
                    };
                  } else {
                    // Formato con descripción: { description, columns }
                    return {
                      tableName: tableName,
                      description: getLocalizedComment(tableData.description),
                      columns: (tableData.columns || []).map(col => ({
                        name: col.Name || col.name || '',
                        type: col.Type || col.type || 'string',
                        // Acepta Comment/comment (formato Glue) y description (formato del data_dictionary).
                        comment: getLocalizedComment(col.Comment || col.comment || col.description)
                      }))
                    };
                  }
                });
              } else if (Array.isArray(dictData)) {
                // Estructura antigua (array directo)
                transformedDictionary = dictData.map(item => ({
                  tableName: item.tableName || item.term || 'Sin nombre',
                  description: item.description || item.definition || '',
                  columns: item.columns || []
                }));
              }

              setDataDictionary(transformedDictionary);
              console.log('[SIDEBAR] Data dictionary loaded:', transformedDictionary.length, 'tables');
            }
          }
        } catch (error) {
          console.error('[SIDEBAR] Error loading data dictionary:', error);
        } finally {
          setIsLoadingDictionary(false);
        }
      }
    };
    fetchDataDictionary();
  }, [user, language]); // Recargar cuando cambia el idioma para mostrar comentarios en el idioma correcto

  // Expandir reporte actual
  useEffect(() => {
    if (currentReportId && !expandedReports[currentReportId]) {
      setExpandedReports(prev => ({ ...prev, [currentReportId]: true }));
    }
  }, [currentReportId]);

  // Listener para actualizar consultas guardadas
  useEffect(() => {
    const handleQuerySaved = () => {
      const fetchQueries = async () => {
        if (user) {
          try {
            const url = `${STRANDS_API_URL}${endpoint_get_saved_consults}?id_user=${encodeURIComponent(user.username)}`;
            const res = await fetch(url);
            const response = await res.json();
            if (response?.saved_consults) {
              const sorted = [...response.saved_consults].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
              setSavedQueries(sorted);
            }
          } catch (error) {
            console.error('Error refreshing saved queries:', error);
          }
        }
      };
      fetchQueries();
    };
    document.addEventListener('querySaved', handleQuerySaved);
    return () => document.removeEventListener('querySaved', handleQuerySaved);
  }, [user]);

  // Listener para actualizar título de conversación
  useEffect(() => {
    const handleTitleUpdated = (event) => {
      const { conversationId, title } = event.detail;
      console.log('[SIDEBAR] Título actualizado:', conversationId, title);
      setConversations(prev => prev.map(conv =>
        conv.id_conversation === conversationId
          ? { ...conv, description: title }
          : conv
      ));
    };
    document.addEventListener('conversationTitleUpdated', handleTitleUpdated);
    return () => document.removeEventListener('conversationTitleUpdated', handleTitleUpdated);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  const addConversation = async () => {
    try {
      const url = `${STRANDS_API_URL}${endpoint_add}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_user: user.username, type_name: "text" })
      });
      const response = await res.json();
      if (response?.id_conversation) {
        navigate(`/chat/${response.id_conversation}`);
        setConversations(prev => [{
          id_conversation: response.id_conversation,
          description: response.description || "Nueva consulta",
          type_name: "text"
        }, ...prev]);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const handleSelectQuery = async (query) => {
    // Si NO estamos en una conversacion (otra pestaña), crear una nueva y navegar con el query en state
    if (!location.pathname.startsWith('/chat/')) {
      try {
        const res = await fetch(`${STRANDS_API_URL}${endpoint_add}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_user: user.username, type_name: 'text' })
        });
        const response = await res.json();
        if (response?.id_conversation) {
          setConversations(prev => [{
            id_conversation: response.id_conversation,
            description: response.description || 'Nueva consulta',
            type_name: 'text'
          }, ...prev]);
          navigate(`/chat/${response.id_conversation}`, { state: { pendingQuery: query } });
        }
      } catch (error) {
        console.error('Error creating conversation for saved query:', error);
      }
      if (window.innerWidth < 768 && toggleSidebar) toggleSidebar();
      return;
    }

    // Ya estamos en /chat/:id — comportamiento existente: solo pegar en el textarea
    if (typeof setMessage === 'function') {
      setMessage(query);
    }
    const customEvent = new CustomEvent('savedQuerySelected', { detail: { query }, bubbles: true });
    document.dispatchEvent(customEvent);
    setTimeout(() => {
      const textarea = document.querySelector('.message-input');
      if (textarea) {
        textarea.value = query;
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        textarea.focus();
      }
    }, 100);
    if (window.innerWidth < 768 && toggleSidebar) toggleSidebar();
  };

  // ─── Custom Reports (Reportes Personalizados) ──────────────────────
  const handleCreateReport = async () => {
    try {
      const res = await fetch(`${STRANDS_API_URL}/api/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user.username, title: 'Nuevo reporte' })
      });
      const data = await res.json();
      if (data?.success && data.report) {
        trackAction('create_report', { report_id: data.report.id, source: 'sidebar' });
        setCustomReports(prev => [{
          id: data.report.id,
          title: data.report.title,
          block_count: 0,
          created_at: data.report.created_at,
          updated_at: data.report.updated_at
        }, ...prev]);
        navigate(`/report/${data.report.id}`);
        if (window.innerWidth < 768 && toggleSidebar) toggleSidebar();
      }
    } catch (error) {
      console.error('Error creating custom report:', error);
    }
  };

  const handleSelectReport = (reportId) => {
    navigate(`/report/${reportId}`);
    if (window.innerWidth < 768 && toggleSidebar) toggleSidebar();
  };

  const handleReportPageClick = (reportId, pageName) => {
    if (reportId !== currentReportId && onReportChange) onReportChange(reportId);
    if (onReportPageChange) onReportPageChange(pageName);
    if (window.innerWidth < 768 && toggleSidebar) toggleSidebar();
  };

  const handleDeleteRequest = (type, id, name) => {
    setModalData({ type, id, name });
    setShowModal(true);
  };

  const confirmDelete = async () => {
    try {
      if (modalData.type === 'conversation') {
        await fetch(`${STRANDS_API_URL}${endpoint_delete}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_conversation: modalData.id })
        });
        trackAction('delete_conversation', { conversation_id: modalData.id });
        setConversations(prev => prev.filter(c => c.id_conversation !== modalData.id));
        if (activeChatId === modalData.id) navigate('/');
      } else if (modalData.type === 'query') {
        await fetch(`${STRANDS_API_URL}${endpoint_delete_consult}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_saved: modalData.id })
        });
        trackAction('delete_saved_query', { query_id: modalData.id });
        setSavedQueries(prev => prev.filter(q => q.id_saved !== modalData.id));
      } else if (modalData.type === 'report') {
        await fetch(`${STRANDS_API_URL}/api/reports/${modalData.id}?username=${encodeURIComponent(user.username)}`, {
          method: 'DELETE'
        });
        trackAction('delete_report', { report_id: modalData.id, source: 'sidebar' });
        setCustomReports(prev => prev.filter(r => r.id !== modalData.id));
        if (location.pathname === `/report/${modalData.id}`) navigate('/');
      }
    } catch (error) {
      console.error('Error deleting:', error);
    } finally {
      setShowModal(false);
      setModalData({ type: '', id: '', name: '' });
    }
  };

  const handleSignOut = async () => {
    try {
      localStorage.removeItem('userFeatures');
      localStorage.removeItem('isCantabria');
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Title editing handlers
  const handleStartEditTitle = (convId, currentTitle) => {
    setEditingTitleId(convId);
    setEditingTitleValue(currentTitle || '');
  };

  const handleCancelEditTitle = () => {
    setEditingTitleId(null);
    setEditingTitleValue('');
  };

  const handleSaveTitle = async (convId) => {
    if (!editingTitleValue.trim()) {
      handleCancelEditTitle();
      return;
    }

    setIsSavingTitle(true);
    try {
      const response = await fetch(
        `${STRANDS_API_URL}/update_conversation`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_conversation: convId,
            description: editingTitleValue.trim()
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setConversations(prev => prev.map(conv =>
            conv.id_conversation === convId
              ? { ...conv, description: data.description || editingTitleValue.trim() }
              : conv
          ));
        }
      }
    } catch (error) {
      console.error('Error updating title:', error);
    } finally {
      setIsSavingTitle(false);
      handleCancelEditTitle();
    }
  };

  const handleTitleKeyDown = (e, convId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveTitle(convId);
    } else if (e.key === 'Escape') {
      handleCancelEditTitle();
    }
  };

  // File upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    // Validar tipo de archivo
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
      'application/vnd.ms-excel', // xls
      'text/csv',
      'application/pdf'
    ];

    if (!allowedTypes.includes(file.type)) {
      alert('Tipo de archivo no permitido. Solo se permiten: Excel, CSV, PDF');
      return;
    }

    // Validar tamaño (max 15MB)
    if (file.size > 15 * 1024 * 1024) {
      alert(t('menu.fileTooLarge'));
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('username', user.username);

      const response = await fetch(`${STRANDS_API_URL}/api/files/upload`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Mapear respuesta al formato esperado por el frontend
          const newFile = {
            id: data.file.id,
            file_name: data.file.originalName || data.file.fileName,
            name: data.file.originalName || data.file.fileName,
            file_type: data.file.fileType,
            file_size: data.file.fileSize,
            uploaded_at: new Date().toISOString()
          };
          // Actualizar lista de archivos
          setUploadedFiles(prev => [newFile, ...prev]);
          setUploadProgress(100);

          // Emitir evento para que otros componentes (como el chat) actualicen su lista de archivos
          window.dispatchEvent(new CustomEvent('fileUploaded', { detail: newFile }));
          console.log('[SIDEBAR] Evento fileUploaded emitido');

          setTimeout(() => {
            setIsUploading(false);
            setUploadProgress(0);
          }, 1000);
        } else {
          throw new Error(data.error || 'Error al subir archivo');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al subir archivo');
      }
    } catch (error) {
      console.error('[SIDEBAR] Error uploading file:', error);
      alert(`Error al subir archivo: ${error.message}`);
      setIsUploading(false);
      setUploadProgress(0);
    }

    // Limpiar input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // File delete handler
  const handleDeleteFile = async (fileId, fileName) => {
    if (!window.confirm(`¿Eliminar el archivo "${fileName}"?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${STRANDS_API_URL}/api/files/${fileId}?username=${encodeURIComponent(user.username)}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
        } else {
          throw new Error(data.error || 'Error al eliminar archivo');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar archivo');
      }
    } catch (error) {
      console.error('[SIDEBAR] Error deleting file:', error);
      alert(`Error al eliminar archivo: ${error.message}`);
    }
  };

  // Agent memory handlers
  const handleOpenMemoryModal = async () => {
    setIsLoadingMemory(true);
    setMemoryModalOpen(true);
    try {
      const response = await fetch(
        `${STRANDS_API_URL}/api/user/agent-memory?username=${encodeURIComponent(user.username)}`
      );
      const data = await response.json();
      if (data.success) {
        setAgentMemory(data.memory || '');
      }
    } catch (error) {
      console.error('[SIDEBAR] Error loading agent memory:', error);
    } finally {
      setIsLoadingMemory(false);
    }
  };

  const handleSaveMemory = async () => {
    setIsSavingMemory(true);
    try {
      const response = await fetch(
        `${STRANDS_API_URL}/api/user/agent-memory?username=${encodeURIComponent(user.username)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ memory: agentMemory })
        }
      );
      const data = await response.json();
      if (data.success) {
        trackAction('save_agent_memory', { memory_length: (agentMemory || '').length });
        setMemoryModalOpen(false);
      } else {
        alert(data.error || t('menu.saveError'));
      }
    } catch (error) {
      console.error('[SIDEBAR] Error saving agent memory:', error);
      alert(t('menu.saveMemoryError'));
    } finally {
      setIsSavingMemory(false);
    }
  };

  // Logo dinámico de la organización (multi-tenant)orgLogoUrl ||
  // Si hay un logo de organización, se usa ese. Si no, se usa el logo por defecto.
  const sidebarLogo =  logoDefault;

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  const sidebarContent = (
    <aside className={`sidebar-v2 ${isSidebarVisible ? 'visible' : 'hidden'}`}>
      {/* Header con Logo dinámico de la organización */}
      <header className="sidebar-header">
        <Link to="/" className="logo-link">
          <img
            src={sidebarLogo}
            alt={orgDisplayName || 'Data Bunker'}
            className="sidebar-logo"
            onError={(e) => {
              // Si falla la carga del logo de org, usar el logo por defecto
              e.target.src = logoDefault;
            }}
          />
        </Link>
      </header>

      {/* Contenido principal scrolleable */}
      <nav className="sidebar-nav">
        {/* ═══════════════════════════════════════════════════════════════════════
            MENÚ PRINCIPAL — maqueta cliente (Cantabria Labs)
            Items reales: Analytics (medios), AI Insights (agente), Usuarios,
            Configuración. El resto son accesos de demo en gris (deshabilitados).

            No renderizamos ningún item hasta que la config del usuario haya
            cargado (featuresLoaded). Así los usuarios que no son de Cantabria no
            alcanzan a ver, ni por un instante, módulos que luego se ocultan
            (p.ej. los que dependen de !userFeatures.isDemo).
            ═══════════════════════════════════════════════════════════════════════ */}
        {!featuresLoaded ? (
          <div className="loading-state"><div className="spinner" /><span>{t('common.loading')}</span></div>
        ) : (
        <>
        {/* ═══════════════════════════════════════════════════════════════════════
            SOCIAL MEDIA — monitoreo por scraping + orgánico por API oficial
            ═══════════════════════════════════════════════════════════════════════ */}
        <NavSection
          id="social"
          icon={<FaHashtag />}
          isActive={SECTION_BY_PATH[location.pathname] === 'social'}
          title={t('menu.sectionSocial')}
          isOpen={openSections.includes('social')}
          onToggle={toggleSection}
        >
          <NavItem
            icon={<FaChartBar />}
            label={t('menu.socialMonitoring')}
            isActive={location.pathname === '/'}
            disabled={userFeatures.mediaMonitoring === false}
            disabledTooltip={t('menu.comingSoon')}
            onClick={() => goTo('/')}
          />

          {/* Redes Orgánicas — Instagram, Facebook y TikTok por API oficial, en una
              sola pantalla con pestañas (antes eran tres items). */}
          <NavItem
            icon={<FaInstagram />}
            label={t('menu.organicSocial')}
            isActive={location.pathname === '/social-organic'}
            onClick={() => goTo('/social-organic')}
          />
        </NavSection>

        {/* ═══════════════════════════════════════════════════════════════════════
            PUBLICIDAD — Excel mensual + APIs de Meta Ads y TikTok Ads
            ═══════════════════════════════════════════════════════════════════════ */}
        <NavSection
          id="advertising"
          icon={<FaBullhorn />}
          isActive={SECTION_BY_PATH[location.pathname] === 'advertising'}
          title={t('menu.sectionAdvertising')}
          isOpen={openSections.includes('advertising')}
          onToggle={toggleSection}
        >
          <NavItem
            icon={<FaBullhorn />}
            label={t('menu.digitalCampaigns')}
            isActive={location.pathname === '/campaigns'}
            onClick={() => goTo('/campaigns')}
          />

          {/* Campañas (API) — APIs oficiales de Meta y TikTok Ads (en vivo), en una
              sola pantalla con selector de red. Oculto en demo: "Campañas
              Digitales" ya sirve el fixture del demo. */}
          {!userFeatures.isDemo && (
            <NavItem
              icon={<FaAd />}
              label={t('menu.campaignsApi')}
              isActive={location.pathname === '/campaigns-api'}
              onClick={() => goTo('/campaigns-api')}
            />
          )}
        </NavSection>

        {/* ═══════════════════════════════════════════════════════════════════════
            AI INSIGHTS (real) — módulo del agente, antes "AI Pricing Engine"
            ═══════════════════════════════════════════════════════════════════════ */}
        {userFeatures.chatAgent !== false && (
        <div className="nav-group">
          <NavItem
            icon={<FaLightbulb />}
            label={t('menu.aiInsights')}
            badge={t('menu.badgeNew')}
            badgeType="new"
            expandable
            isOpen={openModule === 'agent'}
            isActive={openModule === 'agent'}
            onClick={() => toggleModule('agent')}
          />

          {openModule === 'agent' && userFeatures.chatAgent !== false && (
            <div className="module-content">
              {/* Sub-sección: Chats */}
              <div className="sub-section">
                <button
                  className={`sub-header ${openSubSection === 'chats' ? 'open' : ''}`}
                  onClick={() => toggleSubSection('chats')}
                >
                  <FaComment className="sub-header-icon" />
                  <span>{t('menu.chat')}</span>
                  <FaChevronDown className={`sub-chevron ${openSubSection === 'chats' ? 'rotated' : ''}`} />
                </button>

                {openSubSection === 'chats' && (
                  <div className="sub-content">
                    <button className="new-chat-btn" onClick={addConversation}>
                      <FaPlus /> {t('menu.newChat')}
                    </button>
                    {isLoading ? (
                      <div className="loading-state"><div className="spinner" /><span>{t('common.loading')}</span></div>
                    ) : conversations.length === 0 ? (
                      <div className="empty-state">{t('menu.noChats')}</div>
                    ) : (
                      conversations.map(conv => (
                        <div
                          key={conv.id_conversation}
                          className={`sub-item conversation-item ${conv.id_conversation === activeChatId ? 'active' : ''}`}
                          onClick={() => {
                            if (editingTitleId !== conv.id_conversation) {
                              navigate(conv.type_name === 'geo' ? `/geo/${conv.id_conversation}` : `/chat/${conv.id_conversation}`);
                            }
                          }}
                        >
                          <span className="sub-icon">
                            <img src={logoChat} alt="" className="chat-icon" />
                          </span>
                          {editingTitleId === conv.id_conversation ? (
                            <input
                              type="text"
                              className="title-edit-input"
                              value={editingTitleValue}
                              onChange={(e) => setEditingTitleValue(e.target.value)}
                              onKeyDown={(e) => handleTitleKeyDown(e, conv.id_conversation)}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                              disabled={isSavingTitle}
                            />
                          ) : (
                            <span className="sub-label" title={conv.description}>
                              {conv.description || t('menu.newChat')}
                            </span>
                          )}
                          {conv.id_conversation === activeChatId && (
                            <div className="conversation-actions" onClick={(e) => e.stopPropagation()}>
                              {editingTitleId === conv.id_conversation ? (
                                <>
                                  <button
                                    className="action-btn save-btn"
                                    onClick={() => handleSaveTitle(conv.id_conversation)}
                                    disabled={isSavingTitle}
                                    title={t('common.save')}
                                  >
                                    <FaCheck size={10} />
                                  </button>
                                  <button
                                    className="action-btn cancel-btn"
                                    onClick={handleCancelEditTitle}
                                    title={t('common.cancel')}
                                  >
                                    <FaTimes size={10} />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="action-btn edit-btn"
                                    onClick={() => handleStartEditTitle(conv.id_conversation, conv.description)}
                                    title={t('menu.editTitle')}
                                  >
                                    <FaEdit size={10} />
                                  </button>
                                  <button
                                    className="action-btn delete-btn"
                                    onClick={() => handleDeleteRequest('conversation', conv.id_conversation, conv.description)}
                                    title={t('common.delete')}
                                  >
                                    <FaTrash size={10} />
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Sub-sección: Consultas Guardadas */}
              <div className="sub-section">
                <button
                  className={`sub-header ${openSubSection === 'saved' ? 'open' : ''}`}
                  onClick={() => toggleSubSection('saved')}
                >
                  <FaBookmark className="sub-header-icon" />
                  <span>{t('menu.savedQueries')}</span>
                  <FaChevronDown className={`sub-chevron ${openSubSection === 'saved' ? 'rotated' : ''}`} />
                </button>

                {openSubSection === 'saved' && (
                  <div className="sub-content">
                    {isLoadingQueries ? (
                      <div className="loading-state"><div className="spinner" /><span>{t('common.loading')}</span></div>
                    ) : savedQueries.length === 0 ? (
                      <div className="empty-state">{t('menu.noSavedQueries')}</div>
                    ) : (
                      savedQueries.map(q => (
                        <SubItem
                          key={q.id_saved}
                          icon={<FaBookmark className="bookmark-icon" />}
                          label={q.consult.length > 35 ? q.consult.substring(0, 35) + '...' : q.consult}
                          onClick={() => handleSelectQuery(q.consult)}
                          showDelete
                          onDelete={() => handleDeleteRequest('query', q.id_saved, q.consult)}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Sub-sección: Reportes Personalizados */}
              <div className="sub-section">
                <button
                  className={`sub-header ${openSubSection === 'reports' ? 'open' : ''}`}
                  onClick={() => toggleSubSection('reports')}
                >
                  <FaFileAlt className="sub-header-icon" />
                  <span>{t('menu.customReports')}</span>
                  <FaChevronDown className={`sub-chevron ${openSubSection === 'reports' ? 'rotated' : ''}`} />
                </button>

                {openSubSection === 'reports' && (
                  <div className="sub-content">
                    <button className="new-chat-btn" onClick={handleCreateReport}>
                      <FaPlus /> {t('menu.newReport')}
                    </button>
                    {isLoadingReports ? (
                      <div className="loading-state"><div className="spinner" /><span>{t('common.loading')}</span></div>
                    ) : customReports.length === 0 ? (
                      <div className="empty-state">{t('menu.noReports')}</div>
                    ) : (
                      customReports.map(rep => (
                        <SubItem
                          key={rep.id}
                          icon={<FaFileAlt className="bookmark-icon" />}
                          label={rep.title.length > 35 ? rep.title.substring(0, 35) + '...' : rep.title}
                          onClick={() => handleSelectReport(rep.id)}
                          showDelete
                          onDelete={() => handleDeleteRequest('report', rep.id, rep.title)}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Sub-sección: Diccionario de Datos */}
              <div className="sub-section">
                <button
                  className={`sub-header ${openSubSection === 'dictionary' ? 'open' : ''}`}
                  onClick={() => toggleSubSection('dictionary')}
                >
                  <FaDatabase className="sub-header-icon" />
                  <span>{t('menu.dataDictionary')}</span>
                  <FaChevronDown className={`sub-chevron ${openSubSection === 'dictionary' ? 'rotated' : ''}`} />
                </button>

                {openSubSection === 'dictionary' && (
                  <div className="sub-content dict-container">
                    {isLoadingDictionary ? (
                      <div className="loading-state"><div className="spinner" /><span>{t('common.loading')}</span></div>
                    ) : dataDictionary.length === 0 ? (
                      <div className="empty-state">{t('menu.noDictionary')}</div>
                    ) : (
                      dataDictionary.map(table => (
                        <DataDictionaryItem
                          key={table.tableName}
                          {...table}
                          isOpen={openTable === table.tableName}
                          toggleTable={(name) => setOpenTable(prev => prev === name ? null : name)}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Sub-sección: Archivos Subidos */}
              <div className="sub-section">
                <button
                  className={`sub-header ${openSubSection === 'files' ? 'open' : ''}`}
                  onClick={() => toggleSubSection('files')}
                >
                  <FaFile className="sub-header-icon" />
                  <span>{t('menu.uploadedFiles')}</span>
                  <FaChevronDown className={`sub-chevron ${openSubSection === 'files' ? 'rotated' : ''}`} />
                </button>

                {openSubSection === 'files' && (
                  <div className="sub-content files-container">
                    {/* Botón de subir archivo */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept=".xlsx,.xls,.csv,.pdf"
                      style={{ display: 'none' }}
                    />
                    <button
                      className="upload-file-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      <FaUpload />
                      {isUploading ? t('menu.uploading') : t('menu.uploadFile')}
                    </button>

                    {/* Barra de progreso */}
                    {isUploading && (
                      <div className="upload-progress">
                        <div
                          className="upload-progress-bar"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    )}

                    {/* Lista de archivos */}
                    {isLoadingFiles ? (
                      <div className="loading-state"><div className="spinner" /><span>{t('common.loading')}</span></div>
                    ) : uploadedFiles.length === 0 ? (
                      <div className="empty-state">{t('menu.noFiles')}</div>
                    ) : (
                      uploadedFiles.map((file, idx) => (
                        <div key={file.id || idx} className="file-item">
                          {getFileIcon(file.file_name || file.name)}
                          <div className="file-info">
                            <span className="file-name">{file.file_name || file.name}</span>
                            {file.uploaded_at && (
                              <span className="file-date">
                                {new Date(file.uploaded_at).toLocaleDateString(language === 'en' ? 'en-US' : language === 'pt' ? 'pt-BR' : 'es-MX')}
                              </span>
                            )}
                          </div>
                          <button
                            className="file-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFile(file.id, file.file_name || file.name);
                            }}
                            title={t('menu.deleteFile')}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Sub-sección: Memoria del Agente */}
              <div className="sub-section">
                <button
                  className="sub-header memory-btn"
                  onClick={handleOpenMemoryModal}
                >
                  <FaBrain className="sub-header-icon" />
                  <span>{t('menu.agentMemory')}</span>
                </button>
              </div>

              {/* Sub-sección: Guía Interactiva */}
              <div className="sub-section">
                <button
                  className="sub-header guide-btn"
                  onClick={() => {
                    navigate('/guia');
                    if (window.innerWidth < 768 && toggleSidebar) toggleSidebar();
                  }}
                >
                  <FaRocket className="sub-header-icon" />
                  <span>{t('menu.interactiveGuide')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            COMERCIAL — fuentes del cliente real, sin equivalente sintético en demo
            ═══════════════════════════════════════════════════════════════════════ */}
        <NavSection
          id="commercial"
          icon={<FaStore />}
          isActive={SECTION_BY_PATH[location.pathname] === 'commercial'}
          title={t('menu.sectionCommercial')}
          isOpen={openSections.includes('commercial')}
          onToggle={toggleSection}
        >
          {/* Venta (Sell Out) — venta al consumidor reportada por las cadenas retail. */}
          {!userFeatures.isDemo && (
            <NavItem
              icon={<FaShoppingBasket />}
              label={t('menu.sales')}
              badge={t('menu.badgeNew')}
              badgeType="new"
              isActive={location.pathname === '/venta'}
              onClick={() => goTo('/venta')}
            />
          )}

          {/* Beneficios Cantabria — portal de promociones, específico de Cantabria. */}
          {!userFeatures.isDemo && (
            <NavItem
              icon={<FaGift />}
              label={t('menu.benefits')}
              isActive={location.pathname === '/beneficios'}
              onClick={() => goTo('/beneficios')}
            />
          )}

          {/* AndromedApp — dashboard operativo desde la réplica de CRM_FARMACIA */}
          {!userFeatures.isDemo && (
            <NavItem
              icon={<FaRocket />}
              label={t('menu.andromedapp')}
              badge={t('menu.badgeNew')}
              badgeType="new"
              isActive={location.pathname === '/andromedapp'}
              onClick={() => goTo('/andromedapp')}
            />
          )}
        </NavSection>

        {/* ═══════════════════════════════════════════════════════════════════════
            WEB Y MEDIOS — GA4 + monitoreo de medios por keyword (SerpAPI)
            ═══════════════════════════════════════════════════════════════════════ */}
        <NavSection
          id="webMedia"
          icon={<FaGlobeAmericas />}
          isActive={SECTION_BY_PATH[location.pathname] === 'webMedia'}
          title={t('menu.sectionWebMedia')}
          isOpen={openSections.includes('webMedia')}
          onToggle={toggleSection}
        >
          <NavItem
            icon={<FaGlobe />}
            label={t('menu.webTraffic')}
            isActive={location.pathname === '/traffic'}
            onClick={() => goTo('/traffic')}
          />

          {/* Noticias — mismo gate que Social Media Monitoreo. */}
          <NavItem
            icon={<FaNewspaper />}
            label={t('menu.news')}
            isActive={location.pathname === '/noticias'}
            disabled={userFeatures.mediaMonitoring === false}
            disabledTooltip={t('menu.comingSoon')}
            onClick={() => goTo('/noticias')}
          />
        </NavSection>

        </>
        )}
      </nav>

      {/* ═══════════════════════════════════════════════════════════════════════
          FOOTER: PANEL DE USUARIO + LOGOUT (parte de abajo, como la maqueta)
          ═══════════════════════════════════════════════════════════════════════ */}
      <footer className="sidebar-footer">
        <div
          className={`user-info ${isAdmin ? 'clickable' : ''}`}
          onClick={() => {
            if (isAdmin) {
              navigate('/admin');
              if (window.innerWidth < 768 && toggleSidebar) toggleSidebar();
            }
          }}
          title={isAdmin ? t('menu.goToAdmin') : ''}
        >
          <div className="user-avatar">
            {/* Logo de la organización; si no hay (p.ej. demo Data Bunker) o falla
                la carga, cae al logo por defecto de la app (logo-data-new). */}
            <img
              src={(organizationLogo && !logoError) ? organizationLogo : logoDefault}
              alt={userConfig?.organization?.displayName || 'Data Bunker'}
              className="avatar-logo"
              onError={() => setLogoError(true)}
            />
          </div>
          <div className="user-details">
            <span className="user-name">
              {userConfig?.organization?.displayName || user?.username || t('menu.roleUser')}
            </span>
            <span className="user-role-text">{isAdmin ? t('menu.roleAdmin') : t('menu.roleUser')}</span>
          </div>
        </div>

        <button className="logout-btn" onClick={handleSignOut}>
          <FaSignOutAlt />
          <span>{t('menu.logout')}</span>
        </button>
      </footer>

    </aside>
  );

  // Modal de confirmacion de eliminacion (Portal para escapar el transform del sidebar)
  const deleteModalContent = showModal && createPortal(
    <div className="modal-overlay" onClick={() => setShowModal(false)}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>{t('menu.confirmDelete')}</h3>
        <p>
          {modalData.type === 'conversation' ? t('menu.deleteConversation')
            : modalData.type === 'report' ? t('menu.deleteReport')
            : t('menu.deleteSavedQuery')}
        </p>
        <div className="modal-actions">
          <button className="btn-cancel" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
          <button className="btn-delete" onClick={confirmDelete}>{t('common.delete')}</button>
        </div>
      </div>
    </div>,
    document.body
  );

  // Modal de Memoria del Agente (usando Portal para renderizar fuera del sidebar)
  const memoryModalContent = memoryModalOpen && createPortal(
    <div className="modal-overlay memory-modal-overlay" onClick={() => setMemoryModalOpen(false)}>
      <div className="memory-modal" onClick={e => e.stopPropagation()}>
        <div className="memory-modal-header">
          <h3><FaBrain /> {t('menu.agentMemoryTitle')}</h3>
          <button className="close-btn" onClick={() => setMemoryModalOpen(false)}>
            <FaTimes />
          </button>
        </div>

        <div className="memory-modal-body">
          <p className="memory-description">
            {t('menu.agentMemoryDescription')}
          </p>

          {isLoadingMemory ? (
            <div className="loading-state">
              <div className="spinner" />
              <span>{t('common.loading')}</span>
            </div>
          ) : (
            <>
              <textarea
                className="memory-textarea"
                value={agentMemory}
                onChange={(e) => setAgentMemory(e.target.value)}
                placeholder={t('menu.agentMemoryPlaceholder')}
                maxLength={4000}
              />

              <div className="memory-char-count">
                {agentMemory.length} / 4000
              </div>

              <div className="memory-tips">
                <strong>Tip:</strong> {t('menu.agentMemoryTip')}
              </div>
            </>
          )}
        </div>

        <div className="memory-modal-footer">
          <button
            className="btn-cancel"
            onClick={() => setMemoryModalOpen(false)}
          >
            {t('common.cancel')}
          </button>
          <button
            className="btn-save"
            onClick={handleSaveMemory}
            disabled={isSavingMemory || isLoadingMemory}
          >
            {isSavingMemory ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      {sidebarContent}
      {memoryModalContent}
      {deleteModalContent}
    </>
  );
};

export default SidebarMenu;
